import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { Truck, CheckCircle2, AlertTriangle, MapPin, Clock, Phone, Ruler, ShieldCheck, ArrowRight, Droplets, Sprout, CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';
export function DeliveryPrep() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.5
    }
  };
  const stagger = {
    initial: {
      opacity: 0
    },
    whileInView: {
      opacity: 1
    },
    viewport: {
      once: true
    },
    transition: {
      staggerChildren: 0.1
    }
  };
  const item = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5
    }
  };
  return <div className="pt-24 pb-16 bg-[#fdfbf7] dark:bg-[#222222] min-h-screen" data-id="element-1362">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#f3f0ec] dark:bg-[#292827] overflow-hidden border-b border-stone-200 dark:border-white/10" data-id="element-1363">
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-[#e9e5df]/50 dark:from-[#383635]/30 to-transparent pointer-events-none" data-id="element-1364" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1365">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          damping: 20,
          stiffness: 200
        }} className="w-20 h-20 bg-brand-orange/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-brand-orange/20" data-id="element-1366">
            <Truck className="w-10 h-10 text-brand-orange" data-id="element-1367" />
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1,
          duration: 0.5
        }} className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-brand-charcoal dark:text-white tracking-tight mb-6" data-id="element-1368">
            Prepare for Your <span className="text-brand-orange" data-id="element-1369">Delivery</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.5
        }} className="text-lg md:text-xl text-brand-muted dark:text-stone-300 max-w-2xl mx-auto font-medium" data-id="element-1370">
            Follow this simple guide to ensure a smooth, safe, and successful
            material delivery to your home or job site.
          </motion.p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16" data-id="element-1371">
        {/* Before Delivery Day */}
        <motion.section {...fadeIn} data-id="element-1372">
          <div className="flex items-center gap-4 mb-8" data-id="element-1373">
            <div className="w-12 h-12 bg-[#2e8a44]/10 rounded-xl flex items-center justify-center border border-[#2e8a44]/20" data-id="element-1374">
              <CalendarDays className="w-6 h-6 text-[#2e8a44]" data-id="element-1375" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-1376">
              Before Delivery Day
            </h2>
          </div>
          <motion.div variants={stagger} initial="initial" whileInView="whileInView" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1377">
            {[{
            icon: <Truck className="w-5 h-5" data-id="element-1378" />,
            text: 'Clear the dump area of vehicles, toys, and obstacles'
          }, {
            icon: <MapPin className="w-5 h-5" data-id="element-1379" />,
            text: 'Mark the exact spot where you want material dumped (use a cone, tarp, or flag)'
          }, {
            icon: <ShieldCheck className="w-5 h-5" data-id="element-1380" />,
            text: 'Ensure gate access if fenced and provide gate codes if applicable'
          }, {
            icon: <Ruler className="w-5 h-5" data-id="element-1381" />,
            text: 'Measure clearance: Trucks are 10ft wide, 12ft tall, and 30ft long'
          }, {
            icon: <AlertTriangle className="w-5 h-5" data-id="element-1382" />,
            text: 'Check for low-hanging overhead wires or tree branches'
          }, {
            icon: <CheckCircle2 className="w-5 h-5" data-id="element-1383" />,
            text: 'Confirm someone 18+ will be on-site to receive the delivery'
          }].map((point, i) => <motion.div key={i} variants={item} className="flex items-start gap-3 p-4 bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm" data-id="element-1384">
                <div className="text-brand-orange shrink-0 mt-0.5" data-id="element-1385">
                  {point.icon}
                </div>
                <p className="text-brand-charcoal dark:text-stone-200 font-medium" data-id="element-1386">
                  {point.text}
                </p>
              </motion.div>)}
          </motion.div>
        </motion.section>

        {/* Day of Delivery */}
        <motion.section {...fadeIn} data-id="element-1387">
          <div className="flex items-center gap-4 mb-8" data-id="element-1388">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/20" data-id="element-1389">
              <Clock className="w-6 h-6 text-brand-orange" data-id="element-1390" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-1391">
              Day of Delivery
            </h2>
          </div>
          <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 shadow-sm overflow-hidden" data-id="element-1392">
            <div className="divide-y divide-stone-200 dark:divide-white/10" data-id="element-1393">
              {[{
              icon: <Phone className="w-6 h-6" data-id="element-1394" />,
              title: 'Driver will call',
              desc: 'Expect a call 15-30 minutes before the driver arrives at your location.'
            }, {
              icon: <MapPin className="w-6 h-6" data-id="element-1395" />,
              title: 'Point the way',
              desc: 'Direct the driver to your marked dump spot upon arrival.'
            }, {
              icon: <AlertTriangle className="w-6 h-6" data-id="element-1396" />,
              title: 'Stay clear',
              desc: 'Keep children, pets, and yourself at a safe distance during the dumping process.'
            }, {
              icon: <CheckCircle2 className="w-6 h-6" data-id="element-1397" />,
              title: 'Inspect material',
              desc: 'Verify the material is correct before the driver leaves the site.'
            }].map((step, i) => <div key={i} className="p-6 flex items-start gap-4" data-id="element-1398">
                  <div className="w-10 h-10 rounded-full bg-stone-100 dark:bg-white/5 flex items-center justify-center text-brand-muted shrink-0" data-id="element-1399">
                    {step.icon}
                  </div>
                  <div data-id="element-1400">
                    <h3 className="text-lg font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-1401">
                      {step.title}
                    </h3>
                    <p className="text-brand-muted dark:text-stone-400 font-medium" data-id="element-1402">
                      {step.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.section>

        {/* Access Tips */}
        <motion.section {...fadeIn} data-id="element-1403">
          <div className="flex items-center gap-4 mb-8" data-id="element-1404">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center border border-blue-500/20" data-id="element-1405">
              <Truck className="w-6 h-6 text-blue-500" data-id="element-1406" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-1407">
              Access Tips
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1408">
            <div className="p-6 bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm" data-id="element-1409">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-1410">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-1411" />
                Firm Ground Required
              </h3>
              <p className="text-brand-muted dark:text-stone-400 text-sm font-medium" data-id="element-1412">
                Fully loaded trucks are extremely heavy. They need firm ground
                or pavement to drive on, not soggy lawns or soft dirt, to avoid
                getting stuck or causing damage.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm" data-id="element-1413">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-1414">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-1415" />
                Steep Driveways
              </h3>
              <p className="text-brand-muted dark:text-stone-400 text-sm font-medium" data-id="element-1416">
                Very steep driveways may be unsafe for a dump truck to navigate
                or dump on. In these cases, a street-level dump may be required.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm" data-id="element-1417">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-1418">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-1419" />
                Cul-de-sacs & Tight Turns
              </h3>
              <p className="text-brand-muted dark:text-stone-400 text-sm font-medium" data-id="element-1420">
                Large dump trucks need significant turning radius. Ensure there
                is adequate room to maneuver in cul-de-sacs or tight
                neighborhoods.
              </p>
            </div>
            <div className="p-6 bg-brand-orange/10 dark:bg-brand-orange/10 rounded-xl border border-brand-orange/30 shadow-sm flex flex-col justify-center" data-id="element-1421">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-1422">
                <Phone className="w-5 h-5 text-brand-orange" data-id="element-1423" />
                Unsure about access?
              </h3>
              <p className="text-brand-muted dark:text-stone-300 text-sm font-medium mb-3" data-id="element-1424">
                If you have concerns about your delivery location, call us first
                and we can review a photo of your site.
              </p>
              <a href="tel:206-619-3742" className="inline-flex items-center text-brand-orange font-bold hover:underline" data-id="element-1425">
                Call (206) 619-3742 <ArrowRight className="w-4 h-4 ml-1" data-id="element-1426" />
              </a>
            </div>
          </div>
        </motion.section>

        {/* After Delivery */}
        <motion.section {...fadeIn} data-id="element-1427">
          <div className="flex items-center gap-4 mb-8" data-id="element-1428">
            <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center border border-purple-500/20" data-id="element-1429">
              <Sprout className="w-6 h-6 text-purple-500" data-id="element-1430" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-1431">
              After Delivery
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1432">
            <div className="p-6 bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm text-center" data-id="element-1433">
              <Clock className="w-8 h-8 text-brand-muted mx-auto mb-3" data-id="element-1434" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-1435">
                Spread Quickly
              </h3>
              <p className="text-brand-muted dark:text-stone-400 text-sm font-medium" data-id="element-1436">
                Spread material within 48 hours to avoid settling and compaction
                in the pile.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm text-center" data-id="element-1437">
              <Droplets className="w-8 h-8 text-brand-muted mx-auto mb-3" data-id="element-1438" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-1439">
                Wet Before Compacting
              </h3>
              <p className="text-brand-muted dark:text-stone-400 text-sm font-medium" data-id="element-1440">
                Lightly wet gravel before using a plate compactor for maximum
                density and stability.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm text-center" data-id="element-1441">
              <Sprout className="w-8 h-8 text-brand-muted mx-auto mb-3" data-id="element-1442" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-1443">
                Protect Trees
              </h3>
              <p className="text-brand-muted dark:text-stone-400 text-sm font-medium" data-id="element-1444">
                When spreading bark or mulch, keep it at least 3 inches away
                from tree trunks to prevent rot.
              </p>
            </div>
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section {...fadeIn} className="bg-brand-charcoal dark:bg-[#1a1a1a] rounded-2xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-1445">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 pointer-events-none" data-id="element-1446"></div>
          <div className="relative z-10" data-id="element-1447">
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-white mb-4" data-id="element-1448">
              Ready to order?
            </h2>
            <p className="text-stone-300 text-lg mb-8 max-w-xl mx-auto font-medium" data-id="element-1449">
              Now that you know how to prepare, head over to our Materials Hub
              to select your products and schedule your delivery.
            </p>
            <Link to="/#materials" className="inline-flex items-center justify-center bg-brand-orange hover:bg-brand-orangeHover text-white px-8 py-4 rounded-xl font-extrabold text-lg transition-all shadow-[0_0_20px_rgba(255,140,50,0.3)] hover:shadow-[0_0_25px_rgba(255,140,50,0.5)]" data-id="element-1450">
              Browse Materials Hub
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-1451" />
            </Link>
          </div>
        </motion.section>
      </div>
    </div>;
}